import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Wrapper,
  Container,
  CloseImage,
  MobileNavigation,
  Button,
  MiniHeading,
  FrostBackdrop,
} from "../styles/components/PLP/Navigation";
import NavigationItems from "./Navigation-Items";
import { BREAKPOINTS } from "../styles/variables/Breakpoints";
import gsap from "./Animations";

const LandingNavigation = ({
  collection,
  onNavigate,
  navigationParentRef,
  navigationContainerRef,
  parentContainer,
  activePLP,
  activePDP,
  activatePDP,
  observerScrollRef,
}) => {
  const [activePiecesPopup, setActivePiecesPopup] = useState(false);
  const [activeArchivePopup, setActiveArchivePopup] = useState(false);

  const wrapperRef = useRef(null);
  const overflowContainerRef = useRef(null);
  const frostBackgroundRef = useRef(null);
  const aboutArchiveRef = useRef(null);
  const piecesButtonRef = useRef(null);
  const archiveContainerRef = useRef(null);

  const mm = gsap.matchMedia();

  const navigationSelect = e => {
    const id = e.target.parentElement.getAttribute("id");
    id && onNavigate(id);
  };

  const disableScrollHS = () => {
    let element = document.querySelector("#hs-web");
    if (element) {
      element.style.overflow = "hidden";
    } else {
      element = document.querySelector("#hs-app");
      if (element) {
        element.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
    }
  };

  const handleMobilePieces = () => {
    if (activeArchivePopup) return;

    gsap.killTweensOf(".collection-container");

    if (activePDP) {
      gsap
        .timeline({ onStart: () => (document.body.style.overflow = "hidden") })
        .to(parentContainer.current, {
          autoAlpha: 1,
          duration: 0,
        })
        .to(parentContainer.current, {
          scaleY: 1,
          height: "100vh",
          minHeight: "100vh",
          transformOrigin: "bottom bottom",
          duration: 0.5,
          onComplete: () => {
            const tl = gsap.timeline({});
            tl.to(
              ".collection-container",
              {
                duration: 0.85,
                scrollTo: { y: parentContainer.current, autoKill: false },
                onComplete: () => {
                  disableScrollHS();
                  activatePDP(null, false, null);
                  observerScrollRef.current.enable();
                  document
                    .querySelector(".collection-container")
                    .classList.remove("scroll");
                  document.querySelector(".landing-wrapper").style.minHeight =
                    "100vh";
                },
              },
              0
            );
            if (window !== undefined) {
              tl.to(
                window,
                {
                  duration: 0.85,
                  scrollTo: { y: parentContainer.current, autoKill: false },
                },
                0
              );
            }
          },
        });
    } else {
      setActivePiecesPopup(!activePiecesPopup);

      const tl = gsap.timeline({});
      tl.to(
        overflowContainerRef.current,
        {
          autoAlpha: activePiecesPopup ? 0 : 1,
          height: activePiecesPopup ? 1 : "100%",
          duration: 0.25,
        },
        0
      ).to(
        piecesButtonRef.current.querySelector(".close-image"),
        {
          autoAlpha: activePiecesPopup ? 0 : 1,
        },
        0
      );

      piecesButtonRef.current.classList.toggle("active", !activePiecesPopup);
      aboutArchiveRef.current.classList.toggle("active", activePiecesPopup);
      navigationParentRef.current.classList.toggle(
        "active",
        !activePiecesPopup
      );
    }
  };

  const handleMobileArchive = () => {
    if (activePiecesPopup) return;
    setActiveArchivePopup(!activeArchivePopup);

    document.body.classList.toggle("no-scroll", !activeArchivePopup);

    const tl = gsap.timeline();
    tl.to(
      overflowContainerRef.current,
      {
        autoAlpha: activeArchivePopup ? 0 : 1,
        height: activeArchivePopup ? 1 : "100%",
        duration: 0.25,
      },
      0
    ).to(
      aboutArchiveRef.current.querySelector(".close-image"),
      {
        autoAlpha: activeArchivePopup ? 0 : 1,
      },
      0
    );

    archiveContainerRef.current.classList.toggle("active", !activeArchivePopup);
    piecesButtonRef.current.classList.toggle("active", activeArchivePopup);
  };

  useEffect(() => {
    if (activePiecesPopup || (activeArchivePopup && observerScrollRef)) {
      observerScrollRef.current?.disable();
    } else {
      observerScrollRef.current?.enable();
    }
  }, [activePiecesPopup, activeArchivePopup]);

  const aboutArchiveContent = useMemo(() => {
    return Object.keys(collection).reduce((acc, key) => {
      const item = collection[key];

      if (item.key === "gant--about-section") {
        let archiveContent = item.content.filter(
          content =>
            content.moduleTitle && content.moduleTitle === "about-content"
        );

        archiveContent = archiveContent[0]?.text;

        acc.push({
          key,
          archiveContent,
        });
      }

      return acc;
    }, []);
  }, [collection]);

  const navigationItems = useMemo(() => {
    return Object.keys(collection).reduce((acc, key) => {
      const item = collection[key];

      if (item.key !== "gant--about-section") {
        const pdpPrimaryImages = item.content.filter(
          content =>
            content.moduleTitle && content.moduleTitle === "plp-primary-images"
        );
        const pdpPrimaryImage = pdpPrimaryImages[0]?.images[0];

        const pdpParagraphs = item.content.filter(
          content => content.moduleTitle && content.moduleTitle === "plp-id"
        );

        acc.push({
          key,
          pdpPrimaryImage,
          pdpParagraphsText: pdpParagraphs[0]?.text,
        });
      }

      return acc;
    }, []);
  }, [collection]);

  useEffect(() => {
    if (!navigationContainerRef.current) return;
    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      gsap.set(overflowContainerRef.current, {
        autoAlpha: 0,
        height: 1,
        duration: 0.25,
      });
      gsap.set(wrapperRef.current.querySelectorAll(".close-image"), {
        autoAlpha: 0,
      });
    });
  }, []);

  useEffect(() => {
    wrapperRef.current.classList.toggle("fixed", !!activePLP);
  }, [activePLP]);

  return (
    <Wrapper ref={wrapperRef} $mobile>
      <Container
        ref={navigationContainerRef}
        $active={activePiecesPopup || activeArchivePopup}
      >
        <FrostBackdrop
          ref={frostBackgroundRef}
          $active={activePiecesPopup || activeArchivePopup}
        >
          <MobileNavigation>
            <Button
              ref={aboutArchiveRef}
              $noMargin="left"
              $opened={activeArchivePopup}
              onClick={handleMobileArchive}
              className="active"
            >
              <CloseImage
                $left
                $right
                className="close-image"
                src="https://www.datocms-assets.com/40484/1712076073-close-group.svg"
              />
              <MiniHeading>About Archive</MiniHeading>
            </Button>
            <Button
              ref={piecesButtonRef}
              $noMargin="right"
              $opened={activePiecesPopup}
              onClick={handleMobilePieces}
              className="active"
            >
              <MiniHeading $symbol $active={activePDP}>
                ☜
              </MiniHeading>
              <MiniHeading>All Pieces</MiniHeading>
              <CloseImage
                $right
                className="close-image"
                src="https://www.datocms-assets.com/40484/1712076073-close-group.svg"
              />
            </Button>
          </MobileNavigation>
          <NavigationItems
            overflowContainerRef={overflowContainerRef}
            navigationParentRef={navigationParentRef}
            archiveContainerRef={archiveContainerRef}
            navigationItems={navigationItems}
            aboutArchiveContent={aboutArchiveContent}
            navigationSelect={navigationSelect}
          />
        </FrostBackdrop>
      </Container>
    </Wrapper>
  );
};

export default LandingNavigation;
